import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Star,
  Plus,
  Trash2,
  Save,
  RefreshCw,
  GripVertical,
  Edit2,
  Check,
  ChevronDown,
  ChevronRight,
  Copy
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { toast } from 'sonner';

import { API_BASE_URL } from '../utils/api';
const API_URL = API_BASE_URL;

const CATEGORIES = [
  'Voicemail',
  'Forwarding',
  'DND & Privacy',
  'Call Queue',
  'Recording',
  'Call Parking',
  'Pickup',
  'Utilities',
  'Auto Attendant',
  'Conferencing',
  'Hot Desk',
  'Other'
];

const ROLES = [
  { value: 'standard', label: 'Standard' },
  { value: 'phone_admin', label: 'Phone Admin' },
  { value: 'customer_admin', label: 'Customer Admin' }
];

export default function StarCodeTemplateManager({ provider = 'hero' }) {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [newCode, setNewCode] = useState({
    code: '',
    name: '',
    description: '',
    category: 'Other',
    requires_input: false,
    input_placeholder: 'Number',
    visible_to: ['standard', 'phone_admin', 'customer_admin']
  });

  useEffect(() => {
    fetchTemplate();
  }, [provider]);

  const getToken = () => localStorage.getItem('admin_token');

  const fetchTemplate = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/star-codes/templates`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      const templates = response.data.templates || [];
      const template = templates.find(t => t.provider === provider.toLowerCase());
      
      if (template) {
        setCodes(template.codes || []);
        // Expand all categories
        const expanded = {};
        (template.codes || []).forEach(c => {
          expanded[c.category || 'Other'] = true;
        });
        setExpandedCategories(expanded);
      } else {
        // Load from hardcoded defaults if no template exists
        setCodes([]);
      }
    } catch (error) {
      console.error('Failed to fetch template:', error);
      toast.error('Failed to load star code template');
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/star-codes/templates/${provider}`,
        { codes },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success('Template saved successfully');
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const groupedCodes = codes.reduce((acc, code) => {
    const cat = code.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(code);
    return acc;
  }, {});

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleAddCode = () => {
    if (!newCode.code || !newCode.name) {
      toast.error('Code and name are required');
      return;
    }
    
    const codeToAdd = {
      ...newCode,
      id: `new-${Date.now()}`,
      order: codes.length
    };
    
    setCodes([...codes, codeToAdd]);
    setShowAddDialog(false);
    setNewCode({
      code: '',
      name: '',
      description: '',
      category: 'Other',
      requires_input: false,
      input_placeholder: 'Number',
      visible_to: ['standard', 'phone_admin', 'customer_admin']
    });
    toast.success('Code added. Remember to save the template.');
  };

  const handleDeleteCode = (codeId) => {
    setCodes(codes.filter(c => c.id !== codeId));
    toast.success('Code removed. Remember to save the template.');
  };

  const handleEditCode = (code) => {
    setEditingCode({ ...code });
  };

  const handleSaveEdit = () => {
    setCodes(codes.map(c => 
      c.id === editingCode.id ? editingCode : c
    ));
    setEditingCode(null);
    toast.success('Code updated. Remember to save the template.');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="border-slate-600 text-slate-300">
            {codes.length} codes
          </Badge>
          <Badge variant="outline" className="border-slate-600 text-slate-300">
            {Object.keys(groupedCodes).length} categories
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowAddDialog(true)}
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Code
          </Button>
          <Button 
            size="sm" 
            onClick={saveTemplate} 
            disabled={saving}
            className="bg-red-500 hover:bg-red-600"
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Template
          </Button>
        </div>
      </div>

      {/* Empty State */}
      {codes.length === 0 && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="py-12 text-center">
            <Star className="w-12 h-12 mx-auto text-slate-600 mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No Template Codes</h3>
            <p className="text-slate-400 mb-6">
              This provider has no star code template configured. Add codes or load defaults.
            </p>
            <Button onClick={() => setShowAddDialog(true)} className="bg-red-500 hover:bg-red-600">
              <Plus className="w-4 h-4 mr-2" />
              Add First Code
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Codes by Category */}
      {codes.length > 0 && (
        <div className="space-y-2">
          {Object.entries(groupedCodes).sort(([a], [b]) => a.localeCompare(b)).map(([category, categoryCodes]) => (
            <Collapsible
              key={category}
              open={expandedCategories[category]}
              onOpenChange={() => toggleCategory(category)}
            >
              <Card className="bg-slate-800/50 border-slate-700">
                <CollapsibleTrigger asChild>
                  <CardHeader className="py-3 cursor-pointer hover:bg-slate-700/30 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {expandedCategories[category] ? (
                          <ChevronDown className="w-4 h-4 text-slate-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        )}
                        <CardTitle className="text-sm font-medium text-white">{category}</CardTitle>
                        <Badge variant="secondary" className="text-xs bg-slate-700 text-slate-300">
                          {categoryCodes.length}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-0">
                    <div className="space-y-2">
                      {categoryCodes.map((code) => (
                        <div
                          key={code.id}
                          className="flex items-center gap-3 p-2 rounded-lg bg-slate-700/30 hover:bg-slate-700/50 transition-colors"
                        >
                          <GripVertical className="w-4 h-4 text-slate-500 cursor-grab" />
                          <code className="font-mono text-sm font-medium w-16 text-amber-400">{code.code}</code>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-white truncate">{code.name}</p>
                            {code.description && (
                              <p className="text-xs text-slate-400 truncate">{code.description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {code.requires_input && (
                              <Badge variant="outline" className="text-xs border-slate-600 text-slate-300">
                                Input
                              </Badge>
                            )}
                            {(code.visible_to || []).map(role => (
                              <Badge key={role} className="text-xs bg-slate-700 text-slate-300">
                                {role === 'customer_admin' ? 'Admin' : role === 'phone_admin' ? 'Phone' : 'Std'}
                              </Badge>
                            ))}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-slate-400 hover:text-white"
                              onClick={() => handleEditCode(code)}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-400 hover:text-red-300"
                              onClick={() => handleDeleteCode(code.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          ))}
        </div>
      )}

      {/* Add Code Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Add Star Code</DialogTitle>
            <DialogDescription className="text-slate-400">
              Add a new star code to the {provider} template
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Code *</Label>
                <Input
                  className="bg-slate-900/50 border-slate-600 text-white"
                  placeholder="*72"
                  value={newCode.code}
                  onChange={(e) => setNewCode({ ...newCode, code: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Category</Label>
                <Select
                  value={newCode.category}
                  onValueChange={(value) => setNewCode({ ...newCode, category: value })}
                >
                  <SelectTrigger className="bg-slate-900/50 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Name *</Label>
              <Input
                className="bg-slate-900/50 border-slate-600 text-white"
                placeholder="Forward All Calls"
                value={newCode.name}
                onChange={(e) => setNewCode({ ...newCode, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Description</Label>
              <Input
                className="bg-slate-900/50 border-slate-600 text-white"
                placeholder="Optional description"
                value={newCode.description}
                onChange={(e) => setNewCode({ ...newCode, description: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={newCode.requires_input}
                onCheckedChange={(checked) => setNewCode({ ...newCode, requires_input: checked })}
              />
              <Label className="text-slate-300 cursor-pointer">Requires additional input</Label>
            </div>
            {newCode.requires_input && (
              <div className="space-y-2">
                <Label className="text-slate-300">Input Placeholder</Label>
                <Input
                  className="bg-slate-900/50 border-slate-600 text-white"
                  placeholder="Number"
                  value={newCode.input_placeholder}
                  onChange={(e) => setNewCode({ ...newCode, input_placeholder: e.target.value })}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-slate-300">Visible to</Label>
              <div className="flex gap-4">
                {ROLES.map(role => (
                  <div key={role.value} className="flex items-center gap-2">
                    <Checkbox
                      checked={(newCode.visible_to || []).includes(role.value)}
                      onCheckedChange={(checked) => {
                        const visible = newCode.visible_to || [];
                        setNewCode({
                          ...newCode,
                          visible_to: checked
                            ? [...visible, role.value]
                            : visible.filter(r => r !== role.value)
                        });
                      }}
                    />
                    <Label className="text-sm text-slate-300 cursor-pointer">{role.label}</Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button onClick={handleAddCode} className="bg-red-500 hover:bg-red-600">
              <Plus className="w-4 h-4 mr-2" />
              Add Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Code Dialog */}
      <Dialog open={!!editingCode} onOpenChange={(open) => !open && setEditingCode(null)}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Star Code</DialogTitle>
            <DialogDescription className="text-slate-400">
              Modify the star code configuration
            </DialogDescription>
          </DialogHeader>
          {editingCode && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Code *</Label>
                  <Input
                    className="bg-slate-900/50 border-slate-600 text-white"
                    value={editingCode.code}
                    onChange={(e) => setEditingCode({ ...editingCode, code: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Category</Label>
                  <Select
                    value={editingCode.category}
                    onValueChange={(value) => setEditingCode({ ...editingCode, category: value })}
                  >
                    <SelectTrigger className="bg-slate-900/50 border-slate-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Name *</Label>
                <Input
                  className="bg-slate-900/50 border-slate-600 text-white"
                  value={editingCode.name}
                  onChange={(e) => setEditingCode({ ...editingCode, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Description</Label>
                <Input
                  className="bg-slate-900/50 border-slate-600 text-white"
                  value={editingCode.description || ''}
                  onChange={(e) => setEditingCode({ ...editingCode, description: e.target.value })}
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={editingCode.requires_input}
                  onCheckedChange={(checked) => setEditingCode({ ...editingCode, requires_input: checked })}
                />
                <Label className="text-slate-300 cursor-pointer">Requires additional input</Label>
              </div>
              {editingCode.requires_input && (
                <div className="space-y-2">
                  <Label className="text-slate-300">Input Placeholder</Label>
                  <Input
                    className="bg-slate-900/50 border-slate-600 text-white"
                    value={editingCode.input_placeholder || 'Number'}
                    onChange={(e) => setEditingCode({ ...editingCode, input_placeholder: e.target.value })}
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-slate-300">Visible to</Label>
                <div className="flex gap-4">
                  {ROLES.map(role => (
                    <div key={role.value} className="flex items-center gap-2">
                      <Checkbox
                        checked={(editingCode.visible_to || []).includes(role.value)}
                        onCheckedChange={(checked) => {
                          const visible = editingCode.visible_to || [];
                          setEditingCode({
                            ...editingCode,
                            visible_to: checked
                              ? [...visible, role.value]
                              : visible.filter(r => r !== role.value)
                          });
                        }}
                      />
                      <Label className="text-sm text-slate-300 cursor-pointer">{role.label}</Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingCode(null)} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} className="bg-red-500 hover:bg-red-600">
              <Check className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
